import { Observable } from 'rxjs/Observable';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { exhaustMap, finalize, map, mapTo, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { ReplaySubject } from 'rxjs/ReplaySubject';

export type ColourHex = [number, number, number];
export class MagicBlueBulbService {
private static serviceId = 0xffe5;
private static characteristicId = 0xffe9;
private colourStream: Subject<ColourHex> = new ReplaySubject(1);
private device$: Observable<BluetoothDevice>;
private subscribedStreams: Subscription[] = [];
/**
 * @return {?}
 */
private search(): Observable<BluetoothDevice> {
    if (this.device$) {
      return this.device$;
    }

    this.device$ = fromPromise(
      navigator.bluetooth.requestDevice({
        filters: [
          {
            namePrefix: 'LEDBLE',
          },
        ],
        optionalServices: [MagicBlueBulbService.serviceId],
      })
    );

    return this.device$;
  }
/**
 * @return {?}
 */
public forgetDevice(): void {
    this.device$ = undefined;
  }
/**
 * @return {?}
 */
private connect(): Observable<BluetoothRemoteGATTServer> {
    return this.search().pipe(
      switchMap(
        device =>
          new Observable(observer => {
            device.gatt
              .connect()
              .then(server => {
                observer.next(server);
              })
              .catch(error => {
                observer.error(error);
              });

            return () => device.gatt.disconnect();
          })
      )
    );
  }
/**
 * @return {?}
 */
private getCharacteristic(): Observable<BluetoothRemoteGATTCharacteristic> {
    return this.connect().pipe(
      switchMap(server => server.getPrimaryService(MagicBlueBulbService.serviceId)),
      switchMap(service => service.getCharacteristic(MagicBlueBulbService.characteristicId))
    );
  }
/**
 * @param {?=} stream
 * @return {?}
 */
public listen(stream?: Observable<ColourHex | string>): Observable<ColourHex> {
    if (stream) {
      this.registerColourStream(stream);
    }

    return this.getCharacteristic().pipe(
      switchMap(characteristic => {
        return this.colourStream.pipe(
          // this should really be a debounceMap/auditMap operator or similar to ensure that the last ignored message is retried
          // @see https://github.com/ReactiveX/rxjs/issues/1777
          exhaustMap(colour => {
            const /** @type {?} */ buffer = new Uint8Array([0x56, ...colour, 0x00, 0xf0, 0xaa]);

            return fromPromise(characteristic.writeValue(buffer)).pipe(mapTo(colour));
          })
        );
      }),
      finalize(() => this.subscribedStreams.forEach(s => s.unsubscribe()))
    );
  }
/**
 * @param {?} colourStringOrHex
 * @return {?}
 */
public setColour(colourStringOrHex: ColourHex | string): void {
    this.colourStream.next(this.colourStringOrHexToHex(colourStringOrHex));
  }
/**
 * @param {?} colourString
 * @return {?}
 */
private colourStringToHex(colourString: string): ColourHex {
    return /** @type {?} */(( colourString
      .substr(1)
      .match(/(..?)/g)
      .map(s => parseInt(s, 16)) as ColourHex));
  }
/**
 * @param {?} colourStringOrHex
 * @return {?}
 */
private colourStringOrHexToHex(colourStringOrHex: ColourHex | string): ColourHex {
    return typeof colourStringOrHex === 'string' ? this.colourStringToHex(colourStringOrHex) : colourStringOrHex;
  }
/**
 * @param {?} stream
 * @return {?}
 */
public registerColourStream(stream: Observable<ColourHex | string>): void {
    this.subscribedStreams.push(
      stream.pipe(map(s => this.colourStringOrHexToHex(s))).subscribe(colour => this.colourStream.next(colour))
    );
  }
}

function MagicBlueBulbService_tsickle_Closure_declarations() {
/** @type {?} */
MagicBlueBulbService.serviceId;
/** @type {?} */
MagicBlueBulbService.characteristicId;
/** @type {?} */
MagicBlueBulbService.prototype.colourStream;
/** @type {?} */
MagicBlueBulbService.prototype.device$;
/** @type {?} */
MagicBlueBulbService.prototype.subscribedStreams;
}

